import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, ChevronDown, Phone, Mail, MapPin } from 'lucide-react';
import { TREATMENTS } from '../pages/treatments/TreatmentBase';

const REFERRAL_ITEMS = [
  { label: 'CBCT', to: '/referral/cbct' },
  { label: 'Dental Implants', to: '/referral/dental-implants' },
  { label: 'Extractions', to: '/referral/extractions' },
  { label: 'Endodontics', to: '/referral/endodontics' },
  { label: 'Orthodontics', to: '/referral/orthodontics' },
];

const TREATMENT_NAV_ITEMS = TREATMENTS.map(t => ({ label: t.title, to: `/services/${t.slug}`, icon: t.icon }));

const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Treatments', to: '/services', dropdown: TREATMENT_NAV_ITEMS, dropdownKey: 'treatments', wide: true },
  { label: 'Resources', to: '/resources' },
  { label: 'Referral', to: '/referral', dropdown: REFERRAL_ITEMS, dropdownKey: 'referral' },
  { label: 'Smile Gallery', to: '/smile-gallery' },
  { label: 'Fees & Plans', to: '/fees-plans' },
  { label: 'Contact', to: '/contact' },
];

function NavDropdown({ items, parentTo, parentLabel, isOpen, onClose, wide }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="absolute top-full overflow-hidden z-50"
          style={{
            left: wide ? '50%' : '50%',
            transform: 'translateX(-50%)',
            width: wide ? '34rem' : '13rem',
            background: '#1a3a2a',
            borderRadius: '0 0 12px 12px',
            boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderTop: 'none',
          }}
          initial={{ opacity: 0, y: -6, scaleY: 0.95 }}
          animate={{ opacity: 1, y: 0, scaleY: 1 }}
          exit={{ opacity: 0, y: -4, scaleY: 0.95 }}
          transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
        >
          {parentTo && (
            <>
              <Link to={parentTo} onClick={onClose}
                className="block px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors"
                style={{ color: '#c9a84c' }}>
                {wide ? 'All Treatments' : `All ${parentLabel || ''}`}
              </Link>
              <div style={{ height: '1px', background: 'rgba(201,168,76,0.2)', margin: '0 12px' }} />
            </>
          )}
          {wide ? (
            <div className="grid grid-cols-2 gap-0 p-2">
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} onClick={onClose}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group"
                    style={{ color: 'rgba(255,255,255,0.78)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#c9a84c';
                      e.currentTarget.style.background = 'rgba(201,168,76,0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255,255,255,0.78)';
                      e.currentTarget.style.background = 'transparent';
                    }}>
                    {Icon && <Icon className="w-4 h-4 flex-shrink-0 opacity-70" />}
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ) : (
            items.map((item) => (
              <Link key={item.to} to={item.to} onClick={onClose}
                className="block px-4 text-sm font-medium transition-all duration-150 border-l-2 border-transparent"
                style={{ color: 'rgba(255,255,255,0.78)', paddingTop: '10px', paddingBottom: '10px' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#c9a84c';
                  e.currentTarget.style.borderLeftColor = '#c9a84c';
                  e.currentTarget.style.background = 'rgba(201,168,76,0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.78)';
                  e.currentTarget.style.borderLeftColor = 'transparent';
                  e.currentTarget.style.background = 'transparent';
                }}>
                {item.label}
              </Link>
            ))
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [mobileOpenKey, setMobileOpenKey] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navUlRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileOpenKey(null);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    const handler = (e) => {
      if (navUlRef.current && !navUlRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const isDropdownActive = (link) => {
    if (link.to) return isActive(link.to);
    return link.dropdown?.some((item) => location.pathname.startsWith(item.to));
  };

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50"
      style={{ boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.15)' }}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Contact bar */}
      <div className="hidden lg:block" style={{ backgroundColor: '#0f2318' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between py-2">
          {/* Address + hours */}
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
              <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: '#c9a84c' }} />
              106 Woodlands, North Harrow, HA2 6EW
            </span>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Mon–Fri 9am–5:30pm · Sat by appointment
            </span>
          </div>
          {/* Phone + Email — highlighted */}
          <div className="flex items-center gap-2">
            <a href="mailto:hello@wc4d.co.uk"
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full transition-all"
              style={{ color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#c9a84c'; e.currentTarget.style.borderColor = '#c9a84c'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}>
              <Mail className="w-3 h-3" />
              hello@wc4d.co.uk
            </a>
            <a href="tel:+442088665556"
              className="flex items-center gap-2 text-xs font-bold px-4 py-1 rounded-full transition-all"
              style={{ backgroundColor: '#c9a84c', color: '#0f2318' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#b8973d'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#c9a84c'}>
              <Phone className="w-3 h-3" />
              020 8866 5556
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div style={{ backgroundColor: scrolled ? '#122619' : '#1B3A2D', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <nav className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between py-1">
          {/* Logo */}
          <Link to="/" className="flex items-center pr-3 flex-shrink-0">
            <motion.img
              src={`${process.env.PUBLIC_URL}/logo-tranparent-header.png`}
              alt="Woodlands Centre for Dentistry"
              className="h-14 w-auto"
              style={{ display: 'block' }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            />
          </Link>

          {/* Desktop nav */}
          <ul ref={navUlRef} className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link, i) => {
              const active = isDropdownActive(link);
              if (link.dropdown) {
                const isOpen = openDropdown === link.dropdownKey;
                return (
                  <motion.li key={link.dropdownKey} className="relative"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.07, duration: 0.4 }}
                    onMouseEnter={() => setOpenDropdown(link.dropdownKey)}
                    onMouseLeave={() => setOpenDropdown(null)}>
                    <button
                      aria-expanded={isOpen}
                      aria-haspopup="menu"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenDropdown(openDropdown === link.dropdownKey ? null : link.dropdownKey); }
                        if (e.key === 'Escape') setOpenDropdown(null);
                      }}
                      className={`relative flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold ${
                        active ? 'text-gold font-semibold' : 'text-cream/80 hover:text-gold'
                      }`}>
                      {active && (
                        <motion.span layoutId="nav-pill" className="absolute inset-0 bg-white/10 rounded-full"
                          transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                      )}
                      <span className="relative z-10">{link.label}</span>
                      <motion.span className="relative z-10" animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown className="w-3.5 h-3.5" />
                      </motion.span>
                    </button>
                    <NavDropdown
                      items={link.dropdown}
                      parentTo={link.to}
                      parentLabel={link.label}
                      isOpen={isOpen}
                      onClose={() => setOpenDropdown(null)}
                      wide={link.wide}
                    />
                  </motion.li>
                );
              }
              return (
                <motion.li key={link.to}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.4 }}>
                  <Link
                    to={link.to}
                    aria-current={active ? "page" : undefined}
                    className={`relative px-3 py-2 rounded-full text-sm font-semibold transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold ${
                      active ? 'text-gold font-semibold' : 'text-cream/80 hover:text-gold'
                    }`}>
                    {active && (
                      <motion.span layoutId="nav-pill" className="absolute inset-0 bg-white/10 rounded-full"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                    )}
                    <span className="relative z-10">{link.label}</span>
                  </Link>
                </motion.li>
              );
            })}
          </ul>

          {/* CTA + mobile toggle */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Link to="/contact" className="hidden lg:inline-flex btn-primary text-sm py-2 px-4">
                Book Now
              </Link>
            </motion.div>
            <motion.button
              className="lg:hidden p-2 rounded-lg text-cream hover:bg-white/10 transition-colors"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              whileTap={{ scale: 0.9 }}>
              <AnimatePresence mode="wait" initial={false}>
                {open
                  ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}><X className="w-6 h-6" /></motion.span>
                  : <motion.span key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}><Menu className="w-6 h-6" /></motion.span>
                }
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </nav>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="lg:hidden border-t overflow-hidden"
            style={{ borderColor: 'rgba(255,255,255,0.1)', backgroundColor: '#152E24' }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
            <div className="px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link, i) => {
                if (link.dropdown) {
                  const mobileOpen = mobileOpenKey === link.dropdownKey;
                  const active = isDropdownActive(link);
                  return (
                    <motion.div key={link.dropdownKey}
                      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.06, duration: 0.3 }}>
                      <button
                        onClick={() => setMobileOpenKey(mobileOpen ? null : link.dropdownKey)}
                        aria-expanded={mobileOpen}
                        aria-haspopup="menu"
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') setMobileOpenKey(null);
                        }}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold ${
                          active ? 'bg-white/10 text-gold font-semibold' : 'text-cream/80 hover:text-gold hover:bg-white/5'
                        }`}>
                        {link.label}
                        <motion.span animate={{ rotate: mobileOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                          <ChevronDown className="w-4 h-4" />
                        </motion.span>
                      </button>
                      <AnimatePresence>
                        {mobileOpen && (
                          <motion.div className="overflow-hidden"
                            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                            <div className={`pl-4 pt-1 pb-1 ${link.wide ? 'grid grid-cols-2 gap-0.5' : 'flex flex-col gap-0.5'}`}>
                              {link.to && !link.wide && (
                                <Link to={link.to}
                                  className="block px-4 py-2 rounded-xl text-xs font-semibold text-cream/50 uppercase tracking-widest hover:text-gold transition-colors col-span-2">
                                  All {link.label}
                                </Link>
                              )}
                              {link.wide && link.to && (
                                <Link to={link.to}
                                  className="col-span-2 block px-4 py-2 rounded-xl text-xs font-semibold text-cream/50 uppercase tracking-widest hover:text-gold transition-colors">
                                  View All Treatments
                                </Link>
                              )}
                              {link.dropdown.map((item) => {
                                const Icon = item.icon;
                                return (
                                <Link key={item.to} to={item.to}
                                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                    location.pathname === item.to ? 'text-gold' : 'text-cream/70 hover:text-gold'
                                  }`}>
                                  {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />}
                                  {item.label}
                                </Link>
                              )})}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                }
                return (
                  <motion.div key={link.to}
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.3 }}>
                    <Link to={link.to}
                      className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        isActive(link.to) ? 'bg-white/10 text-gold font-semibold' : 'text-cream/80 hover:text-gold hover:bg-white/5'
                      }`}>
                      {link.label}
                    </Link>
                  </motion.div>
                );
              })}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}>
                <Link to="/contact" className="btn-primary justify-center mt-3 text-sm">Book Appointment</Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
